'use client';

import {
  GraphQLCardExpiryStatus,
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';

import {
  commitmentsQuery,
  paymentMethodsQuery,
  supporterAddressQuery,
} from '../queries';
import { getSupporterAddresses } from '../utils/addresses';
import { useSupporterQuery } from './base';

/**
 * Hook to get payment methods with associated commitments
 * @returns An object containing the payment methods and associated commitments.
 */
export const usePaymentMethodsWithCommitments = () => {
  const {
    data: [paymentMethods = [], commitments = []],
    isLoading,
    isFetching,
    isPending,
  } = useSupporterQuery([paymentMethodsQuery, commitmentsQuery]);

  const commitmentsByPaymentMethodId = commitments?.reduce<
    Record<string, GraphQLCommitment[]>
  >((acc, commitment) => {
    const { paymentMethodId } = commitment;
    if (paymentMethodId) {
      if (!acc[paymentMethodId]) {
        acc[paymentMethodId] = [];
      }
      acc[paymentMethodId].push(commitment);
    }
    return acc;
  }, {});

  return {
    isLoading: isLoading || isFetching || isPending,
    paymentMethods: paymentMethods ?? [],
    commitmentsByPaymentMethodId,
    commitments,
  };
};

/**
 * Hook to get payment methods that have issues to resolve
 * @returns An object containing the payment methods and associated commitments.
 */
export const usePaymentMethodsWithIssues = () => {
  const { isLoading, paymentMethods, commitmentsByPaymentMethodId } =
    usePaymentMethodsWithCommitments();

  let paymentMethodsWithIssues: GraphQLSupporterPaymentMethod[] = [];
  let associatedCommitments: GraphQLCommitment[] = [];

  if (paymentMethods.length) {
    paymentMethodsWithIssues = paymentMethods.filter(
      ({ creditCard }) =>
        creditCard?.expiryStatus === GraphQLCardExpiryStatus.Expired,
    );
  }

  if (paymentMethodsWithIssues.length && commitmentsByPaymentMethodId) {
    const paymentMethodIds = paymentMethodsWithIssues.map(({ id }) => id);
    const associatedCommitmentsArray = Object.entries(
      commitmentsByPaymentMethodId,
    ).filter(([paymentMethodId]) => paymentMethodIds.includes(paymentMethodId));
    associatedCommitments = associatedCommitmentsArray.reduce(
      (arr: GraphQLCommitment[], [_id, commitments]) => {
        const commitmentsWithMissedPayments = commitments.filter(
          ({ isRecurring, hasMissedPayment }) =>
            isRecurring && hasMissedPayment,
        );
        return [...arr, ...commitmentsWithMissedPayments];
      },
      [],
    );
  }

  return {
    isLoading,
    paymentMethodsWithIssues,
    associatedCommitments,
  };
};

export const useSupporterBillingAddresses = () => {
  const {
    data: [supporterAddress, supporterPaymentMethods = []],
    isLoading,
    isFetching,
    isPending,
    isError,
  } = useSupporterQuery([supporterAddressQuery, paymentMethodsQuery]);

  return {
    supporterAddresses: supporterAddress
      ? getSupporterAddresses(supporterAddress, supporterPaymentMethods)
      : undefined,
    isLoading,
    isFetching,
    isPending,
    isError,
  };
};
